/* eslint-disable react-hooks/refs */

// oxlint-disable max-lines-per-function

import { Button, Spinner, Text } from '@change/ds-core-ui';
import { ErrorMessage, TextArea, TextField } from '@change/ds-forms';
import { ReminderIcon } from '@change/ds-icons/ReminderIcon';
import { UploadIcon } from '@change/ds-icons/UploadIcon';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { Translate, TranslatePlural, TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';

import { StepActions } from '#src/app/pages/startAPetitionV2/shared/components';
import { PetitionDescriptionEditor } from '#src/app/shared/components/richTextEditor';

import { BannerActions } from './components/BannerActions';
import { Hero } from './components/Hero';
import { MakeAiImageIcon } from './components/MakeAiImage';
import { useCreate } from './hooks/useCreate';

export const Create: React.FC = () => {
	const { data, actions } = useCreate();
	const { translate } = useI18n();

	const imageButtonIcon =
		data.imageButtonIcon === 'loader' ? (
			<Spinner className="size-4" />
		) : (
			<MakeAiImageIcon className="h-6 w-6" aria-hidden focusable={false} />
		);

	return (
		// NOTE: 88px is the height of the StepActions component and 61px is the height of the header
		// min-h-[calc(100vh-88px-61px)] ensures that the content area takes up at least the full height of the viewport minus the header and step actions
		// grid-rows-[min-content_auto_min-content] ensures that the banner actions are at the top and bottom, with the content area taking up the remaining space
		// mb-22 ensures that there is enough space at the bottom for the StepActions component when it is fixed to the bottom of the viewport
		<div
			className="bg-background-overlay mb-22 grid min-h-[calc(100vh-88px-61px)] grid-rows-[min-content_auto_min-content]"
			key={data.mode}
		>
			<form data-qa="edit-form" ref={data.formRef}>
				<Text as="h1" variant="heading" className="sr-only">
					<Translate value="corgi.start_a_petition.edit.heading" />
				</Text>
				<Grid className="pt-4 md:pt-8">
					<div className="bg-background-base col-span-full overflow-hidden rounded-tl-lg rounded-tr-lg">
						{/* Heading + Image */}
						<Hero status={data.jobStatus} imageSrc={data.imageSrc}>
							<div className="[&_.react-aria-FieldError>div>span:last-child]:text-(--color-stroke-error-weak)!">
								<TextField
									name="ask"
									isInvalid={!!data.errors.ask}
									errorMessage={data.errors.ask}
									label={translate('corgi.start_a_petition.edit.ask.input_label')}
									defaultValue={data.ask}
									placeholder={translate('corgi.start_a_petition.edit.ask.input_placeholder')}
									maxLength={90}
									input={
										<TextArea
											onChange={() => actions.resetFieldError('ask')}
											onBlur={actions.persistAsk}
											rows={2}
											className={cn([
												'h-[unset] w-full resize-none bg-transparent',
												'text-size-heading-xl-sm text-typography-inverse-strong md:text-size-heading-xl-md lg:text-size-heading-xl',

												// default state - stroke-inverse-weak border + no outline
												'border! border-[#e1dde9]!', // --Gray-Gray-Slate-Solid-300

												// focus styles - stroke-focus border + stroke-focus outline with no offset
												'focus:border-stroke-focus! focus:border!',
												'focus:[outline:1px_solid_var(--color-stroke-focus)]!',

												// invalid state - stroke-error-strong border + no outline
												'invalid:border-stroke-error-strong! invalid:border-2!',
												'invalid:outline-none!',

												// invalid focus styles - stroke-error-strong border + stroke-focus outline with offset
												'invalid:focus:border-stroke-error-strong! invalid:focus:border-2!',
												'invalid:focus:[outline:3px_solid_var(--color-stroke-focus)]! invalid:focus:outline-offset-2!',

												// hover state - background color change
												'hover:bg-[#403a4980]!', // --Gray-Gray-Slate-500

												// placeholder text color
												'placeholder-stroke-inverse-disabled!', // --Gray-Gray-Neutral-Dark-30
											])}
										/>
									}
								/>
							</div>
						</Hero>
						<Grid className="gap-4 px-0 pt-4 pb-8 md:gap-8 md:pb-14">
							{/* Image Action Buttons */}
							<div
								aria-live="polite"
								className="col-span-full px-4 md:col-span-6 md:col-start-2 md:px-0 lg:col-span-8 lg:col-start-3"
							>
								<div className="flex flex-row flex-wrap items-center gap-2">
									<Button
										size="x-small"
										leadingIcon={imageButtonIcon}
										data-qa="generate-ai-image-button"
										className="flex-[1_1_auto] md:flex-initial"
										onClick={actions.regenerateAiImage}
										disabled={data.imageRegenerationDisabled}
										aria-describedby={data.imageRegenerationCount > 1 ? 'regenerate-image-button-subtext' : undefined}
									>
										<TypedTranslate value={data.imageButtonLabel} />
									</Button>
									<Button
										size="x-small"
										leadingIcon={<UploadIcon />}
										variant="secondary"
										data-qa="upload-image-button"
										className="flex-[1_1_auto] md:flex-initial"
										onClick={actions.uploadImage}
									>
										<Translate value="corgi.start_a_petition.edit.image_action_buttons.use_my_photo" />
									</Button>
								</div>
								{/* Image generation count - only show after first regeneration */}
								{data.imageRegenerationCount > 1 && (
									<div className="flex flex-row flex-wrap items-center gap-2 py-2">
										<Text className="text-typography-weak" size="small" id="regenerate-image-button-subtext">
											{data.imageRegenerationLeft === 0 ? (
												<Translate value="corgi.start_a_petition.edit.image_action_buttons.zero_image_regenerations_subtext" />
											) : (
												<TranslatePlural
													value="corgi.start_a_petition.edit.image_action_buttons.regenerate_subtext"
													count={data.imageRegenerationLeft}
													replacements={{ count: data.imageRegenerationLeft }}
												/>
											)}
										</Text>
									</div>
								)}
							</div>

							{/* Description */}
							<div className="col-span-full min-h-[50vh] space-y-4 px-4 md:col-span-6 md:col-start-2 md:px-0 lg:col-span-8 lg:col-start-3">
								<Text as="h2" variant="heading" aria-hidden>
									<Translate value="corgi.start_a_petition.edit.description.heading" />
								</Text>
								<div className="space-y-1">
									<PetitionDescriptionEditor
										hideLabel
										ariaLabel={translate('corgi.sap.description.a11y.aria_label')}
										descriptiveLabel={translate('corgi.sap.description.a11y.descriptive_label')}
										initialValue={data.description}
										data-qa="edit-description"
										textareaName="description"
										autosize
										onEditorChange={() => {
											actions.resetFieldError('description');
										}}
										onBlur={actions.persistDescription}
									/>
									{data.errors.description && <ErrorMessage>{data.errors.description}</ErrorMessage>}
								</div>
							</div>
						</Grid>
					</div>
				</Grid>
				<BannerActions
					title={<Translate value="corgi.start_a_petition.create.banner_actions.bottom.heading" />}
					description={<Translate value="corgi.start_a_petition.create.banner_actions.bottom.description" />}
					onPrimaryAction={actions.toOptionalPreview}
					icon={<ReminderIcon />}
				/>
				<StepActions
					className="md:bg-background-base! px-0! py-4! md:fixed! md:right-0! md:bottom-0! md:left-0! md:border-t! md:shadow-md! [&>div]:container"
					type="navigation"
					onPrimaryAction={actions.submit}
					onSecondaryAction={actions.back}
				/>
			</form>
		</div>
	);
};
